"use client";

import { Children, cloneElement, isValidElement, useMemo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "@/components/ui/MemoizedMarkdown";
import { tokenInfoKey, useSimTokensInfo } from "@/hooks/useSimTokenInfo";
import { parseTextWithAddresses, type InlineTokenData } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
interface TemplateDescriptionMarkdownProps {
  content: string;
  /** Raw (address, chainId) pairs to resolve via Sim Dune and render as token badges. */
  tokenRefs?: {
    tokenAddress: string;
    chainId: number;
  }[];
}

// Markdown collapses runs of blank lines into a single paragraph break. To
// let authors add extra vertical space, replace each blank line beyond the
// first with an NBSP-only paragraph (which renders as a visible empty line
// and survives the MDXEditor round-trip). One blank line in source (3
// consecutive newlines) is the natural paragraph separator and gets no
// spacer — extras start at the second blank line.
export function preserveBlankLines(markdown: string): string {
  return markdown.replace(/\n{4,}/g, match => {
    const extraBlankLines = match.length - 3;
    return "\n\n" + "\u00A0\n\n".repeat(extraBlankLines);
  });
}

/**
 * Recursively walk React children and replace string nodes with address-parsed
 * output so Ethereum addresses, org wallets, and token badges render inline
 * within markdown-generated elements (paragraphs, list items, etc.).
 */
function processTextNodes(children: ReactNode, tokensMap?: Map<string, InlineTokenData>): ReactNode {
  return Children.map(children, child => {
    if (typeof child === "string") {
      const parsed = parseTextWithAddresses(child, tokensMap);
      if (parsed.length === 1 && parsed[0] === child) return child;
      return parsed;
    }
    if (isValidElement<{
      children?: ReactNode;
    }>(child) && child.props.children) {
      return cloneElement(child, {}, processTextNodes(child.props.children, tokensMap));
    }
    return child;
  });
}

/**
 * Markdown renderer for template descriptions that extends the shared
 * markdownComponents with inline address/token badge support.
 *
 * Capabilities beyond base MemoizedMarkdown:
 * - Ethereum address → clickable badge with avatar
 * - Token address → symbol/logo badge (via tokenRefs → Sim Dune lookup)
 * - Org wallet ID (wal_*) → wallet badge
 */
export function TemplateDescriptionMarkdown({
  content,
  tokenRefs
}: TemplateDescriptionMarkdownProps) {
  const {
    tokenInfoByKey
  } = useSimTokensInfo(tokenRefs || [], !!tokenRefs?.length);
  const tokensMap = useMemo(() => {
    const map = new Map<string, InlineTokenData>();
    for (const ref of tokenRefs || []) {
      const info = tokenInfoByKey[tokenInfoKey(ref.tokenAddress, ref.chainId)];
      if (!info) continue;
      map.set(ref.tokenAddress.toLowerCase(), {
        address: ref.tokenAddress,
        symbol: info.symbol,
        name: info.name || info.symbol,
        chainId: info.chain_id,
        logoUrl: info.logo || info.token_metadata?.logo
      });
    }
    return map.size > 0 ? map : undefined;
  }, [tokenRefs, tokenInfoByKey]);
  const process = (children: ReactNode) => processTextNodes(children, tokensMap);

  // Extend shared markdown components with address-processing wrappers
  // for text-bearing elements (p, li, td).
  const components = useMemo(() => ({
    ...markdownComponents,
    p: ({
      children
    }: {
      children?: ReactNode;
    }) => <p className="my-2 leading-relaxed first:mt-0 last:mb-0">{process(children)}</p>,
    li: ({
      children
    }: {
      children?: ReactNode;
    }) => <li className="leading-relaxed">{process(children)}</li>,
    td: ({
      children
    }: {
      children?: ReactNode;
    }) => <td className="text-ds-text-secondary px-3 py-2">{process(children)}</td>
  }),
  // tokensMap reference changes when token data resolves — rebuild components.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tokensMap]);
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {preserveBlankLines(content)}
    </ReactMarkdown>;
}